// Removed Ant Design message import to prevent UI interference

export interface WebSocketConfig {
  url: string;
  protocols?: string | string[];
  onOpen?: (event: Event) => void;
  onMessage?: (event: MessageEvent) => void;
  onError?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  reconnectAttempts?: number;
  reconnectInterval?: number;
  heartbeatInterval?: number;
  heartbeatMessage?: string | object;
}

export class WebSocketRequest {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectCount = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private manualClose = false;

  constructor(config: WebSocketConfig) {
    this.config = {
      reconnectAttempts: 2, // Reduced from 3
      reconnectInterval: 15000, // Increased from 3000ms to 15 seconds
      heartbeatInterval: 60000, // Increased from 30000ms to 60 seconds
      heartbeatMessage: 'ping',
      ...config
    };
  }

  // 连接 WebSocket
  connect(): void {
    try {
      this.ws = new WebSocket(this.config.url, this.config.protocols);
      this.addEventListeners();
    } catch (error) {
      // Removed message notification to prevent page refreshes
      console.error('Failed to create WebSocket connection:', error);
      this.handleReconnect();
    }
  }

  // 添加事件监听器
  private addEventListeners(): void {
    if (!this.ws) return;

    this.ws.onopen = (event: Event) => {
      this.reconnectCount = 0;
      this.startHeartbeat();
      this.config.onOpen?.(event);
      // Removed message notification to prevent page refreshes
      console.log('WebSocket connected');
    };

    this.ws.onmessage = (event: MessageEvent) => {
      // 如果收到的是心跳响应，不触发 onMessage
      if (event.data === 'pong') return;
      this.config.onMessage?.(event);
    };

    this.ws.onerror = (event: Event) => {
      this.config.onError?.(event);
      // Removed message notification to prevent page refreshes
      console.error('WebSocket error occurred');
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.stopHeartbeat();
      this.config.onClose?.(event);
      
      if (!this.manualClose) {
        // Removed message notification to prevent page refreshes
        console.warn('WebSocket connection closed');
        this.handleReconnect();
      }
    };
  }

  // 发送消息
  send(data: string | object): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Removed message notification to prevent page refreshes
      console.error('WebSocket not connected, cannot send message');
      return;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.ws.send(message);
    } catch (error) {
      // Removed message notification to prevent page refreshes
      console.error('Failed to send WebSocket message:', error);
    }
  }

  // 关闭连接
  close(): void {
    this.manualClose = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  // 重新连接
  private handleReconnect(): void {
    if (
      this.reconnectCount < (this.config.reconnectAttempts || 3) &&
      !this.manualClose
    ) {
      this.reconnectTimer = setTimeout(() => {
        this.reconnectCount++;
        // Removed message notification to prevent page refreshes
        console.info(`Attempting WebSocket reconnection ${this.reconnectCount}...`);
        this.connect();
      }, this.config.reconnectInterval);
    } else if (!this.manualClose) {
      // Removed message notification to prevent page refreshes
      console.error('WebSocket reconnection failed after maximum attempts');
    }
  }

  // 开始心跳
  private startHeartbeat(): void {
    if (this.config.heartbeatInterval && this.config.heartbeatMessage) {
      this.heartbeatTimer = setInterval(() => {
        this.send(this.config.heartbeatMessage!);
      }, this.config.heartbeatInterval);
    }
  }

  // 停止心跳
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // 获取连接状态
  getState(): number {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }

  // 检查是否已连接
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// WebSocket 状态常量
export const WebSocketState = {
  CONNECTING: WebSocket.CONNECTING,
  OPEN: WebSocket.OPEN,
  CLOSING: WebSocket.CLOSING,
  CLOSED: WebSocket.CLOSED
};

// 创建 WebSocket 实例的工厂函数
export const createWebSocket = (config: WebSocketConfig): WebSocketRequest => {
  return new WebSocketRequest(config);
}; 